import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { 
  Aperture, 
  ChevronLeft, 
  History, 
  Trash2, 
  Eye, 
  AlertTriangle,
  Calendar,
  Filter,
  X,
  Loader2,
  ImageIcon,
  Scan,
  RefreshCw
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Gallery = () => {
  const navigate = useNavigate();
  const [detections, setDetections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDetection, setSelectedDetection] = useState(null);

  const fetchDetections = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/detections?limit=50`);
      setDetections(response.data);
    } catch (error) {
      console.error("Failed to fetch detections:", error);
      toast.error("Failed to load detection history");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchDetections();
  }, []);

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/detection/${id}`);
      setDetections(prev => prev.filter(d => d.id !== id));
      toast.success("Detection deleted");
      if (selectedDetection?.id === id) {
        setSelectedDetection(null);
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete detection");
    }
  };

  const formatDate = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown date';
    }
  };

  const getConfidenceColor = (score) => {
    if (score > 70) return '#00f2ea';
    if (score > 40) return '#ffd700';
    return '#ff2e2e';
  };

  return (
    <div className="min-h-screen bg-[#02040a] flex">
      {/* Noise Overlay */}
      <div className="noise-overlay" />

      {/* Sidebar */}
      <aside className="w-20 lg:w-64 bg-[#0a0f1e] border-r border-white/5 flex flex-col">
        <div className="p-4 lg:p-6 border-b border-white/5">
          <div className="flex items-center gap-3">
            <Aperture className="w-8 h-8 text-[#00f2ea]" />
            <span className="hidden lg:block font-unbounded font-bold text-white">Skybot</span>
          </div>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            <li>
              <button
                data-testid="nav-dashboard-btn"
                onClick={() => navigate('/dashboard')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-white/5 transition-colors"
              >
                <Scan className="w-5 h-5" />
                <span className="hidden lg:block font-exo2">Dashboard</span>
              </button>
            </li>
            <li>
              <button
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg bg-[#00f2ea]/10 text-[#00f2ea] transition-colors"
              >
                <History className="w-5 h-5" />
                <span className="hidden lg:block font-exo2">Gallery</span>
              </button>
            </li>
          </ul>
        </nav>

        <div className="p-4 border-t border-white/5">
          <button
            data-testid="back-home-btn"
            onClick={() => navigate("/")}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-slate-400 hover:bg-white/5 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="hidden lg:block font-exo2">Back</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 lg:p-8 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-unbounded font-bold text-2xl lg:text-3xl text-white flex items-center gap-3">
              <History className="w-8 h-8 text-[#00f2ea]" />
              Detection Gallery
            </h1>
            <p className="font-exo2 text-slate-400 mt-2">
              {detections.length} detection{detections.length !== 1 ? 's' : ''} recorded
            </p>
          </div>
          <button
            data-testid="refresh-btn"
            onClick={fetchDetections}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Loading State */}
        {isLoading && detections.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="w-12 h-12 text-[#00f2ea] animate-spin mb-4" />
            <p className="font-exo2 text-slate-400">Loading detections...</p>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && detections.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24">
            <ImageIcon className="w-16 h-16 text-slate-600 mb-4" />
            <h3 className="font-unbounded font-semibold text-xl text-white mb-2">No Detections Yet</h3>
            <p className="font-exo2 text-slate-400 mb-6 text-center max-w-md">
              Start scanning the sky to build your detection history
            </p>
            <button
              data-testid="start-scanning-btn"
              onClick={() => navigate('/dashboard')}
              className="btn-primary"
            >
              <span>Start Scanning</span>
            </button>
          </div>
        )}

        {/* Gallery Grid */}
        {detections.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {detections.map((detection, index) => (
              <motion.div
                key={detection.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="glass-panel overflow-hidden cursor-pointer hover:border-[#00f2ea]/30 transition-colors"
                onClick={() => setSelectedDetection(detection)}
                data-testid={`detection-card-${index}`}
              >
                {/* Image */}
                <div className="aspect-video bg-[#02040a] relative overflow-hidden">
                  {detection.enhanced_image_base64 ? (
                    <img
                      src={detection.enhanced_image_base64.startsWith('data:') 
                        ? detection.enhanced_image_base64 
                        : `data:image/jpeg;base64,${detection.enhanced_image_base64}`}
                      alt="Detection"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-12 h-12 text-slate-700" />
                    </div>
                  )}

                  {/* Anomaly Badge */}
                  {detection.anomalies_detected.length > 0 && (
                    <div className="absolute top-3 left-3 flex items-center gap-1 px-2 py-1 bg-[#ff2e2e]/90 rounded text-xs font-mono text-white">
                      <AlertTriangle className="w-3 h-3" />
                      {detection.anomalies_detected.length} anomal{detection.anomalies_detected.length > 1 ? 'ies' : 'y'}
                    </div>
                  )}

                  {/* Premium Badge */}
                  {detection.is_premium && (
                    <div className="absolute top-3 right-3 px-2 py-1 bg-[#ffd700]/90 rounded text-xs font-mono text-[#02040a] font-bold">
                      PREMIUM
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="p-4">
                  {/* Analysis Preview */}
                  <div className="mb-3">
                    <p className="font-mono text-xs text-slate-400 line-clamp-2">
                      {detection.analysis?.substring(0, 120) || 'No analysis available'}...
                    </p>
                  </div>

                  {/* Confidence */}
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-mono text-xs text-slate-500 uppercase">Confidence</span>
                    <span 
                      className="font-unbounded font-bold text-sm"
                      style={{ color: getConfidenceColor(detection.confidence_score) }}
                    >
                      {detection.confidence_score.toFixed(1)}%
                    </span>
                  </div>

                  {/* Confidence Bar */}
                  <div className="confidence-meter mb-4">
                    <div 
                      className="confidence-meter-fill"
                      style={{ 
                        width: `${detection.confidence_score}%`,
                        background: getConfidenceColor(detection.confidence_score)
                      }}
                    />
                  </div>

                  {/* Filters */}
                  {detection.filters_applied.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-3">
                      {detection.filters_applied.slice(0, 3).map((filter, i) => (
                        <span key={i} className="px-2 py-1 bg-[#00f2ea]/10 text-[#00f2ea] rounded text-xs font-mono">
                          {filter.replace('_', ' ')}
                        </span>
                      ))}
                      {detection.filters_applied.length > 3 && (
                        <span className="px-2 py-1 bg-white/5 text-slate-500 rounded text-xs font-mono">
                          +{detection.filters_applied.length - 3}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Date & Actions */}
                  <div className="flex items-center justify-between pt-3 border-t border-white/5">
                    <span className="font-mono text-xs text-slate-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(detection.timestamp)}
                    </span>
                    <button
                      onClick={(e) => handleDelete(detection.id, e)}
                      className="text-slate-500 hover:text-[#ff2e2e] transition-colors"
                      data-testid={`delete-btn-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedDetection && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#02040a]/90 backdrop-blur-sm"
            onClick={() => setSelectedDetection(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="glass-panel max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="detection-modal"
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b border-white/5">
                <h2 className="font-unbounded font-bold text-xl text-white flex items-center gap-3">
                  <Eye className="w-6 h-6 text-[#00f2ea]" />
                  Detection Details
                </h2>
                <button
                  onClick={() => setSelectedDetection(null)}
                  className="text-slate-400 hover:text-white transition-colors"
                  data-testid="close-modal-btn"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {/* Image */}
                <div className="aspect-video bg-[#02040a] rounded-lg overflow-hidden mb-6">
                  {selectedDetection.enhanced_image_base64 ? (
                    <img
                      src={selectedDetection.enhanced_image_base64.startsWith('data:') 
                        ? selectedDetection.enhanced_image_base64 
                        : `data:image/jpeg;base64,${selectedDetection.enhanced_image_base64}`}
                      alt="Detection"
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ImageIcon className="w-16 h-16 text-slate-700" />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Stats */}
                  <div>
                    <h3 className="font-exo2 font-medium text-white mb-4">Statistics</h3>
                    
                    {/* Confidence */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-xs text-slate-500 uppercase">Confidence Score</span>
                        <span 
                          className="font-unbounded font-bold"
                          style={{ color: getConfidenceColor(selectedDetection.confidence_score) }}
                        >
                          {selectedDetection.confidence_score.toFixed(1)}%
                        </span>
                      </div>
                      <div className="confidence-meter h-3">
                        <div 
                          className="confidence-meter-fill"
                          style={{ 
                            width: `${selectedDetection.confidence_score}%`,
                            background: getConfidenceColor(selectedDetection.confidence_score)
                          }}
                        />
                      </div>
                    </div>

                    {/* Meta */}
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="font-exo2 text-slate-400">Date</span>
                        <span className="font-mono text-slate-300">{formatDate(selectedDetection.timestamp)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-exo2 text-slate-400">Mode</span>
                        <span className="font-mono text-slate-300">{selectedDetection.is_premium ? 'Premium' : 'Standard'}</span>
                      </div>
                    </div>

                    {/* Filters */}
                    {selectedDetection.filters_applied.length > 0 && (
                      <div className="mt-4">
                        <span className="font-mono text-xs text-slate-500 uppercase block mb-2">Filters Applied</span>
                        <div className="flex flex-wrap gap-2">
                          {selectedDetection.filters_applied.map((filter, i) => (
                            <span key={i} className="px-3 py-1 bg-[#00f2ea]/10 text-[#00f2ea] rounded text-sm font-mono">
                              {filter.replace('_', ' ')}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Anomalies */}
                  <div>
                    <h3 className="font-exo2 font-medium text-white mb-4 flex items-center gap-2">
                      <AlertTriangle className={`w-5 h-5 ${selectedDetection.anomalies_detected.length > 0 ? 'text-[#ff2e2e]' : 'text-slate-500'}`} />
                      Anomalies Detected
                    </h3>
                    
                    {selectedDetection.anomalies_detected.length > 0 ? (
                      <div className="space-y-2">
                        {selectedDetection.anomalies_detected.map((anomaly, i) => (
                          <div key={i} className="anomaly-item">
                            <p className="font-exo2 text-sm text-white">{anomaly}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="font-exo2 text-slate-500">No anomalies detected in this scan.</p>
                    )}
                  </div>
                </div>

                {/* Full Analysis */}
                <div className="mt-6">
                  <h3 className="font-exo2 font-medium text-white mb-4">Full AI Analysis</h3>
                  <div className="bg-[#02040a] p-4 rounded-lg max-h-64 overflow-y-auto">
                    <p className="font-mono text-xs text-slate-400 whitespace-pre-wrap">
                      {selectedDetection.analysis}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
